import { useEffect, useMemo, useState } from 'react'

import { FbModal as Modal } from '../../components/UI/Modal'
import { Campaign } from '../../models/campaign'
import { CompanyTypeSlug } from '../../models/companies'
import { useEditCampaign } from './campaigns_api'
import apiService from '../../services/api'
import { useUsers } from 'services/users'
import {
  CampaignFormFieldsV2,
  type CampaignOwnerOption,
  type CampaignSelectOption,
  getCampaignOwnerDisplayName,
  getCampaignOwnerInitials,
  getColorOption,
} from './CampaignFormFieldsV2'

interface Props {
  show: boolean
  onHide: () => void
  campaign: Campaign
  campaignId: number
}

export function EditCampaignModalV2({
  show,
  onHide,
  campaign,
  campaignId,
}: Props) {
  const api = apiService()
  const { data: users = [], isLoading: isUsersLoading } = useUsers()
  const { data: products = [], isLoading: isProductsLoading } =
    api.useGetProducts()
  const { data: categories, isLoading: isCampaignTypesLoading } =
    api.useGetActiveCompanyCategoriesAndTypes(false)
  const editMutation = useEditCampaign()

  const [name, setName] = useState(campaign.name)
  const [launchDate, setLaunchDate] = useState(campaign.launch_date ?? '')
  const [launchDateType, setLaunchDateType] = useState<'set' | 'skip'>(
    campaign.launch_date ? 'set' : 'skip'
  )
  const [ownerId, setOwnerId] = useState<number | null>(
    campaign.owner_id ?? null
  )
  const [productId, setProductId] = useState<number | null>(
    campaign.product?.id ?? null
  )
  const [description, setDescription] = useState(campaign.description ?? '')
  const [color, setColor] = useState(getColorOption(campaign.color))
  const [notificationRecipientIds, setNotificationRecipientIds] = useState<
    number[]
  >(campaign.notification_recipient_ids ?? [])
  const [typeSlug, setTypeSlug] = useState<CompanyTypeSlug | null>(
    campaign.type?.slug ?? null
  )

  useEffect(() => {
    if (!show) return
    setName(campaign.name)
    setLaunchDate(campaign.launch_date ?? '')
    setLaunchDateType(campaign.launch_date ? 'set' : 'skip')
    setOwnerId(campaign.owner_id ?? null)
    setProductId(campaign.product?.id ?? null)
    setDescription(campaign.description ?? '')
    setColor(getColorOption(campaign.color))
    setNotificationRecipientIds(campaign.notification_recipient_ids ?? [])
    setTypeSlug(campaign.type?.slug ?? null)
  }, [show, campaign])

  const ownerOptions = useMemo<CampaignOwnerOption[]>(() => {
    return [...users]
      .sort((a, b) =>
        getCampaignOwnerDisplayName(a).localeCompare(
          getCampaignOwnerDisplayName(b),
          undefined,
          {
            sensitivity: 'base',
          }
        )
      )
      .map((user) => ({
        value: user.id,
        label: getCampaignOwnerDisplayName(user),
        email: user.email,
        initials: getCampaignOwnerInitials(user),
      }))
  }, [users])

  const campaignTypeOptions = useMemo<
    CampaignSelectOption<CompanyTypeSlug>[]
  >(() => {
    const options: CampaignSelectOption<CompanyTypeSlug>[] = []
    categories?.forEach((category) => {
      category.sub_categories.forEach((subCategory) => {
        options.push({
          value: subCategory.slug,
          label: `${category.name} - ${subCategory.name}`,
        })
      })
    })
    return options
  }, [categories])

  const productOptions = useMemo<CampaignSelectOption<number>[]>(
    () =>
      products.map((product) => ({
        value: product.id,
        label: product.name,
      })),
    [products]
  )

  const hasRecipientChanges = useMemo(() => {
    const current = [...(campaign.notification_recipient_ids ?? [])].sort(
      (a, b) => a - b
    )
    const next = [...notificationRecipientIds].sort((a, b) => a - b)
    return (
      current.length !== next.length ||
      current.some((id, index) => id !== next[index])
    )
  }, [notificationRecipientIds, campaign.notification_recipient_ids])

  const hasChanges = useMemo(() => {
    return (
      name !== campaign.name ||
      launchDate !== (campaign.launch_date ?? '') ||
      launchDateType !== (campaign.launch_date ? 'set' : 'skip') ||
      ownerId !== campaign.owner_id ||
      productId !== (campaign.product?.id ?? null) ||
      description !== (campaign.description ?? '') ||
      color.background !== getColorOption(campaign.color).background ||
      typeSlug !== (campaign.type?.slug ?? null) ||
      hasRecipientChanges
    )
  }, [
    name,
    launchDate,
    launchDateType,
    ownerId,
    productId,
    description,
    color,
    typeSlug,
    campaign,
    hasRecipientChanges,
  ])

  const handleSave = async () => {
    if (!hasChanges || !name.trim()) return

    const updates: Record<string, unknown> = {}

    if (name !== campaign.name) {
      updates.name = name.trim()
    }

    if (
      launchDateType === 'set' &&
      launchDate !== (campaign.launch_date ?? '')
    ) {
      updates.launch_date = launchDate || null
    } else if (launchDateType === 'skip' && campaign.launch_date) {
      updates.launch_date = null
    }

    if (ownerId !== campaign.owner_id) {
      updates.owner = ownerId
    }

    if (productId !== (campaign.product?.id ?? null)) {
      updates.product_id = productId
    }

    if (description !== (campaign.description ?? '')) {
      updates.description = description
    }

    if (color.background !== getColorOption(campaign.color).background) {
      updates.color = color.background
    }

    if (typeSlug !== (campaign.type?.slug ?? null)) {
      // Slug (or null to clear) is what the backend persists.
      updates.type_slug = typeSlug
      // The resolved segment object drives the optimistic cache update (mirrors
      // how `product` is passed); the backend ignores it.
      const selectedSubCategory =
        categories
          ?.flatMap((category) => category.sub_categories)
          .find((subCategory) => subCategory.slug === typeSlug) ?? null
      updates.type = selectedSubCategory
        ? {
            id: selectedSubCategory.id,
            name: selectedSubCategory.name,
            slug: selectedSubCategory.slug,
          }
        : null
    }

    if (hasRecipientChanges) {
      updates.notification_recipient_ids = notificationRecipientIds
    }

    if (Object.keys(updates).length === 0) {
      onHide()
      return
    }

    const selectedProduct =
      productId == null
        ? null
        : (products.find((product) => product.id === productId) ?? null)

    try {
      await editMutation.mutateAsync({
        id: campaignId,
        product: selectedProduct
          ? {
              id: selectedProduct.id,
              name: selectedProduct.name,
            }
          : undefined,
        ...updates,
      })
      onHide()
    } catch {
      // Mutation handles toast
    }
  }

  return (
    <Modal
      open={show}
      onOpenChange={(open: boolean) => !open && onHide()}
      title="Edit Campaign"
      description="Update your campaign details."
      size="lg"
      onAccept={handleSave}
      acceptButtonText="Save Changes"
      blockAccept={!hasChanges || !name.trim()}
      loading={editMutation.isPending}
    >
      <div className="space-y-5">
        <CampaignFormFieldsV2
          name={name}
          onNameChange={setName}
          campaignTypeOptions={campaignTypeOptions}
          campaignTypeValue={typeSlug}
          onCampaignTypeChange={setTypeSlug}
          campaignTypeLoading={isCampaignTypesLoading}
          launchDate={launchDate}
          onLaunchDateChange={setLaunchDate}
          launchDateType={launchDateType}
          onLaunchDateTypeChange={setLaunchDateType}
          ownerOptions={ownerOptions}
          ownerValue={ownerId}
          onOwnerChange={setOwnerId}
          ownerLoading={isUsersLoading}
          productOptions={productOptions}
          productValue={productId}
          onProductChange={setProductId}
          productLoading={isProductsLoading}
          description={description}
          onDescriptionChange={setDescription}
          color={color}
          onColorChange={setColor}
          notificationRecipientIds={notificationRecipientIds}
          onNotificationRecipientsChange={setNotificationRecipientIds}
        />
      </div>
    </Modal>
  )
}
